<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  appId: { type: [String, Number], required: true },
  // [{ id, key, label }] — static groups a member can be assigned to.
  memberGroups: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const editingId = ref(null);
const email = ref('');
// First/last are a UI split over the single `name` column: combined on save,
// split on open (first token = first name, remainder = last name).
const firstName = ref('');
const lastName = ref('');
// E.164 phone (e.g. "+5491133334444"); flows into the linked Contact server-side.
const phone = ref('');
const role = ref('member');
const selectedGroups = ref([]);
// Manual password reset (edit only): admin types a new password. Left blank keeps
// the member's current password untouched (the server drops blank passwords).
const password = ref('');
const showPassword = ref(false);
const isSaving = ref(false);
// The single-use magic link returned right after an invite, shown copyable once.
const inviteLink = ref('');
// Set when the server returns 409: the typed email/phone clashes with an existing
// contact. `kind: 'field'` is resolvable (admin picks which value wins); `kind: 'split'`
// (email and phone belong to two different contacts) can't be reconciled here.
const conflict = ref(null);
// Per-field decision, keyed by field name: 'keep' = contact's current value, 'change' = typed.
const conflictChoices = ref({});

const isEditing = computed(() => editingId.value !== null);
const isSplitConflict = computed(() => conflict.value?.kind === 'split');
const title = computed(() =>
  isEditing.value
    ? t('APP_BUILDER.MEMBERS.FORM.EDIT_TITLE')
    : t('APP_BUILDER.MEMBERS.FORM.INVITE_TITLE')
);
const confirmLabel = computed(() => {
  if (conflict.value?.kind === 'field')
    return t('APP_BUILDER.MEMBERS.CONFLICT.RESOLVE');
  return isEditing.value
    ? t('APP_BUILDER.MEMBERS.FORM.SAVE')
    : t('APP_BUILDER.MEMBERS.FORM.SEND_INVITE');
});

// Mirror of the server-side PortalMember::PASSWORD_COMPLEXITY policy: at least 8
// characters with an uppercase letter, a number, and a symbol. Blank is valid here
// (an empty field keeps the member's current password).
const PASSWORD_POLICY = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const passwordValid = computed(
  () => !password.value || PASSWORD_POLICY.test(password.value)
);

function reset() {
  editingId.value = null;
  email.value = '';
  firstName.value = '';
  lastName.value = '';
  phone.value = '';
  role.value = 'member';
  selectedGroups.value = [];
  password.value = '';
  showPassword.value = false;
  inviteLink.value = '';
  conflict.value = null;
  conflictChoices.value = {};
}

// Open for invite (no member) or edit (existing member).
function open(member = null) {
  reset();
  if (member) {
    editingId.value = member.id;
    email.value = member.email;
    const parts = (member.name || '').trim().split(/\s+/).filter(Boolean);
    firstName.value = parts.shift() || '';
    lastName.value = parts.join(' ');
    phone.value = member.phone || '';
    role.value = member.role || 'member';
    selectedGroups.value = [...(member.group_keys || [])];
  }
  dialogRef.value?.open();
}

function toggleGroup(key) {
  const idx = selectedGroups.value.indexOf(key);
  if (idx === -1) selectedGroups.value.push(key);
  else selectedGroups.value.splice(idx, 1);
}

function buildPayload(overrides = {}) {
  const fullName = [firstName.value.trim(), lastName.value.trim()]
    .filter(Boolean)
    .join(' ');
  return {
    appId: props.appId,
    email: email.value.trim(),
    name: fullName,
    phone: phone.value,
    role: role.value,
    group_keys: selectedGroups.value,
    ...overrides,
  };
}

async function submit(overrides = {}) {
  if (isEditing.value) {
    return store.dispatch('appBuilder/updateMember', {
      id: editingId.value,
      // Sent as-is; the server ignores a blank password (keeps the current one).
      password: password.value,
      ...buildPayload(overrides),
    });
  }
  return store.dispatch('appBuilder/createMember', buildPayload(overrides));
}

function onSaved(member) {
  if (isEditing.value) {
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.UPDATED'));
    emit('saved');
    dialogRef.value?.close();
    return;
  }
  useAlert(t('APP_BUILDER.MEMBERS.ALERTS.INVITED'));
  emit('saved');
  // Keep the dialog open to surface the copyable invite link.
  inviteLink.value = member?.invite_link || '';
  if (!inviteLink.value) dialogRef.value?.close();
}

function onError(error) {
  const status = error?.response?.status;
  if (status === 409) {
    conflict.value = error.response?.data?.conflict || null;
    conflictChoices.value = {};
    (conflict.value?.fields || []).forEach(f => {
      conflictChoices.value[f.field] = 'keep';
    });
    if (conflict.value?.kind === 'split')
      useAlert(t('APP_BUILDER.MEMBERS.CONFLICT.SPLIT'));
    return;
  }
  // Surface the server's validation message (password policy, email taken, …) when
  // present; otherwise fall back to the generic duplicate/save messages.
  const serverMessage = error?.response?.data?.message;
  useAlert(
    status === 422
      ? serverMessage || t('APP_BUILDER.MEMBERS.ALERTS.DUPLICATE')
      : t('APP_BUILDER.ALERTS.SAVE_ERROR')
  );
}

async function confirm() {
  if (!email.value.trim()) return;
  isSaving.value = true;
  try {
    onSaved(await submit());
  } catch (error) {
    onError(error);
  } finally {
    isSaving.value = false;
  }
}

// Writes a resolved value back into the form field it came from. Name is combined in
// storage, so it splits back into the first/last inputs (same rule as open()).
function applyChosenField(field, value) {
  if (field === 'email') {
    email.value = value;
  } else if (field === 'phone') {
    phone.value = value;
  } else if (field === 'name') {
    const parts = (value || '').trim().split(/\s+/).filter(Boolean);
    firstName.value = parts.shift() || '';
    lastName.value = parts.join(' ');
  }
}

// Re-submit after the admin chose which value wins for each conflicting field. The chosen
// values are written into the form (member ↔ contact stay in sync server-side) and the
// matched contact is pinned so the server updates it instead of re-detecting the clash.
async function resolveConflict() {
  const current = conflict.value;
  if (!current || current.kind !== 'field') return;
  current.fields.forEach(f => {
    const chosen =
      conflictChoices.value[f.field] === 'keep' ? f.existing : f.incoming;
    applyChosenField(f.field, chosen);
  });

  isSaving.value = true;
  try {
    const result = await submit({
      contact_id: current.contact.id,
      resolve_conflict: true,
    });
    conflict.value = null;
    onSaved(result);
  } catch (error) {
    onError(error);
  } finally {
    isSaving.value = false;
  }
}

// The dialog's single confirm button does double duty: resolve a field conflict when one
// is showing, otherwise the normal save/invite.
function onConfirm() {
  if (conflict.value?.kind === 'field') return resolveConflict();
  return confirm();
}

async function copyInviteLink() {
  try {
    await navigator.clipboard.writeText(inviteLink.value);
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.LINK_COPIED'));
  } catch {
    // Clipboard may be unavailable; the field stays selectable for manual copy.
  }
}

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="title"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="
      !email.trim() || !passwordValid || !!inviteLink || isSplitConflict
    "
    :is-loading="isSaving"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex gap-3">
        <div class="flex-1">
          <Input
            v-model="firstName"
            :label="t('APP_BUILDER.MEMBERS.FORM.FIRST_NAME')"
            :placeholder="t('APP_BUILDER.MEMBERS.FORM.FIRST_NAME_PLACEHOLDER')"
            autofocus
          />
        </div>
        <div class="flex-1">
          <Input
            v-model="lastName"
            :label="t('APP_BUILDER.MEMBERS.FORM.LAST_NAME')"
            :placeholder="t('APP_BUILDER.MEMBERS.FORM.LAST_NAME_PLACEHOLDER')"
          />
        </div>
      </div>
      <Input
        v-model="email"
        type="email"
        :label="t('APP_BUILDER.MEMBERS.FORM.EMAIL')"
        :placeholder="t('APP_BUILDER.MEMBERS.FORM.EMAIL_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('APP_BUILDER.MEMBERS.FORM.PHONE') }}
        </label>
        <PhoneNumberInput
          v-model="phone"
          :placeholder="t('APP_BUILDER.MEMBERS.FORM.PHONE_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('APP_BUILDER.MEMBERS.FORM.ROLE') }}
        </label>
        <select
          v-model="role"
          class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
        >
          <option value="member">
            {{ t('APP_BUILDER.MEMBERS.ROLE.MEMBER') }}
          </option>
          <option value="admin">
            {{ t('APP_BUILDER.MEMBERS.ROLE.ADMIN') }}
          </option>
        </select>
        <span class="text-xs text-n-slate-10">
          {{ t('APP_BUILDER.MEMBERS.FORM.ROLE_HINT') }}
        </span>
      </div>

      <div v-if="memberGroups.length" class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('APP_BUILDER.MEMBERS.FORM.GROUPS') }}
        </label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="group in memberGroups"
            :key="group.id || group.key"
            type="button"
            class="px-2.5 py-1 rounded-full text-xs border transition-colors"
            :class="
              selectedGroups.includes(group.key)
                ? 'bg-n-brand/10 border-n-brand text-n-brand'
                : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            :aria-pressed="selectedGroups.includes(group.key)"
            @click="toggleGroup(group.key)"
          >
            {{ group.label || group.key }}
          </button>
        </div>
      </div>

      <!-- Manual password reset (edit only) -->
      <div v-if="isEditing" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('APP_BUILDER.MEMBERS.FORM.PASSWORD') }}
        </label>
        <div
          class="flex items-stretch h-10 rounded-lg border border-n-weak bg-n-alpha-1 overflow-hidden"
        >
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            :placeholder="t('APP_BUILDER.MEMBERS.FORM.PASSWORD_PLACEHOLDER')"
            class="flex-1 min-w-0 h-full px-3 bg-transparent border-0 text-sm text-n-slate-12 focus:outline-none focus:ring-0"
          />
          <button
            type="button"
            class="shrink-0 w-10 h-full flex items-center justify-center border-s border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :aria-label="
              showPassword
                ? t('APP_BUILDER.MEMBERS.FORM.HIDE_PASSWORD')
                : t('APP_BUILDER.MEMBERS.FORM.SHOW_PASSWORD')
            "
            @click="showPassword = !showPassword"
          >
            <!-- Inline SVG (matches AppEditor's icon approach) for reliable centering -->
            <svg
              v-if="showPassword"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
              <path
                d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
              />
              <path
                d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
              />
              <line x1="2" x2="22" y1="2" y2="22" />
            </svg>
            <svg
              v-else
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>
        <span
          class="text-xs"
          :class="
            password && !passwordValid ? 'text-n-ruby-11' : 'text-n-slate-10'
          "
        >
          {{
            password && !passwordValid
              ? t('APP_BUILDER.MEMBERS.FORM.PASSWORD_INVALID')
              : t('APP_BUILDER.MEMBERS.FORM.PASSWORD_HINT')
          }}
        </span>
      </div>

      <!-- Conflict: the typed email/phone matches an existing contact whose other field
           differs. The admin picks which value wins; resolving re-submits. -->
      <div
        v-if="conflict && conflict.kind === 'field'"
        class="flex flex-col gap-3 rounded-lg border border-n-amber-7 p-3 bg-n-amber-2"
      >
        <div class="flex flex-col gap-0.5">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('APP_BUILDER.MEMBERS.CONFLICT.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              t('APP_BUILDER.MEMBERS.CONFLICT.INTRO', {
                name: conflict.contact.name || conflict.contact.email,
              })
            }}
          </span>
        </div>
        <div
          v-for="f in conflict.fields"
          :key="f.field"
          class="flex flex-col gap-1 pt-2 border-t border-n-amber-7 first:border-0 first:pt-0"
        >
          <span class="text-xs font-medium text-n-slate-12">
            {{ t(`APP_BUILDER.MEMBERS.CONFLICT.FIELD_${f.field.toUpperCase()}`) }}
          </span>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="conflictChoices[f.field]" type="radio" value="keep" />
            {{ t('APP_BUILDER.MEMBERS.CONFLICT.KEEP', { value: f.existing }) }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="conflictChoices[f.field]" type="radio" value="change" />
            {{ t('APP_BUILDER.MEMBERS.CONFLICT.CHANGE', { value: f.incoming }) }}
          </label>
        </div>
      </div>

      <!-- Split conflict: email and phone belong to two different contacts; can't merge. -->
      <div
        v-else-if="conflict && conflict.kind === 'split'"
        class="rounded-lg border border-n-ruby-7 p-3 bg-n-ruby-2 text-xs text-n-ruby-11"
      >
        {{ t('APP_BUILDER.MEMBERS.CONFLICT.SPLIT') }}
      </div>

      <!-- Post-invite: copyable single-use sign-in link -->
      <div
        v-if="inviteLink"
        class="flex flex-col gap-1.5 rounded-lg border border-n-weak p-3 bg-n-alpha-1"
      >
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('APP_BUILDER.MEMBERS.FORM.INVITE_LINK_TITLE') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ t('APP_BUILDER.MEMBERS.FORM.INVITE_LINK_HINT') }}
        </span>
        <div class="flex items-center gap-2">
          <input
            :value="inviteLink"
            readonly
            class="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-n-background border border-n-weak text-xs text-n-slate-11 font-mono"
            @focus="$event.target.select()"
          />
          <button
            type="button"
            class="shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium bg-n-brand text-white hover:opacity-90"
            @click="copyInviteLink"
          >
            {{ t('APP_BUILDER.MEMBERS.FORM.COPY') }}
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
